import { useToast } from '@shared/hooks/use-toast';
import { faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@shared/components/ui/button';
import { ProductCombobox } from '@shared/components/ui/ProductCombobox';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { useProductSearch } from '@shared/hooks/useProductSearch';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreatePack } from '../../hooks/useCreatePack';
import { useUpdatePack } from '../../hooks/useUpdatePack';
import { CreatePackRequest, Pack } from '../../models/Pack';
import { UpdatePackRequest } from '../../services/PackService';
import { useTenantChildren } from '@modules/Products/CreateEdit/hooks/useTenantChildren';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';

interface PackLine {
  product_id: number;
  quantity: number;
  product_name?: string;
  product_code?: string;
  product_image?: string;
}

interface ProductToAdd {
  product_id: string;
  quantity: number;
}

interface PackModalProps {
  mode: 'create' | 'edit';
}

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithPack: (pack: Pack) => void;
}

const PackModal = forwardRef<ExtendedModalCenterRef, PackModalProps>(({ mode }, ref) => {
  // 🎯 Hook de traducciones
  const { t } = useTranslation('packsModule');
  const { toast } = useToast();

  const {
    tenantChildren,
    isTenant3pl,
    isLoading: tenantChildrenLoading,
    error: tenantChildrenError,
  } = useTenantChildren();

  const [selectedTenantChildId, setSelectedTenantChildId] = useState<number | null>(null);
  const prevTenantChildRef = useRef<number | null | undefined>(undefined);
  const prevClientForTitleFocusRef = useRef<number | null>(null);
  const packTitleSectionRef = useRef<HTMLDivElement>(null);
  const packTitleInputRef = useRef<HTMLInputElement>(null);
  const modalScrollContainerRef = useRef<HTMLDivElement>(null);

  const isCreateFor3pl = mode === 'create' && isTenant3pl;
  const productCatalogEnabled = !isCreateFor3pl || Boolean(selectedTenantChildId);

  const {
    searchProductsForCombobox,
    searchProducts,
    products,
    isLoading: productsLoading,
  } = useProductSearch(
    isCreateFor3pl
      ? {
          tenantChildId: selectedTenantChildId ?? undefined,
          enabled: productCatalogEnabled,
        }
      : undefined,
  );

  // 🎯 Hooks condicionales según el modo
  const createPackHook = useCreatePack();
  const updatePackHook = useUpdatePack();

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [pack, setPack] = useState<Pack | null>(null);
  const [title, setTitle] = useState('');
  const [lines, setLines] = useState<PackLine[]>([]);

  // Estado para el formulario de agregar producto
  const [productToAdd, setProductToAdd] = useState<ProductToAdd>({
    product_id: '',
    quantity: 1,
  });

  const clientOptions = useMemo(
    () =>
      tenantChildren.map((child) => ({
        label: child.name,
        value: String(child.id),
      })),
    [tenantChildren],
  );
  const hasClientOptions = clientOptions.length > 0;

  // 🎯 Al cambiar de cliente en creación 3PL, vaciar líneas para no mezclar catálogos
  useEffect(() => {
    if (!isCreateFor3pl) return;
    const id = selectedTenantChildId;
    if (prevTenantChildRef.current != null && prevTenantChildRef.current !== id) {
      setLines([]);
      setProductToAdd({ product_id: '', quantity: 1 });
    }
    prevTenantChildRef.current = id;
  }, [isCreateFor3pl, selectedTenantChildId]);

  // Tras elegir (o cambiar) cliente en 3PL: scroll animado (más lento que smooth nativo) y foco al final
  useEffect(() => {
    if (!isCreateFor3pl) return;
    if (selectedTenantChildId === null) {
      prevClientForTitleFocusRef.current = null;
      return;
    }
    const prev = prevClientForTitleFocusRef.current;
    if (prev === selectedTenantChildId) return;
    prevClientForTitleFocusRef.current = selectedTenantChildId;

    let cancelled = false;
    let rafLayout1 = 0;
    let rafLayout2 = 0;
    let rafScroll = 0;

    const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    // Duración del scroll para que el usuario perciba el desplazamiento dentro del modal
    const SCROLL_MS = 850;

    rafLayout1 = requestAnimationFrame(() => {
      rafLayout2 = requestAnimationFrame(() => {
        if (cancelled) return;
        const container = modalScrollContainerRef.current;
        const target = packTitleSectionRef.current;
        if (!container || !target) {
          packTitleInputRef.current?.focus({ preventScroll: true });
          return;
        }

        const padding = 16;
        const targetRect = target.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const delta = targetRect.top - containerRect.top - padding;
        const startScroll = container.scrollTop;
        const goalScroll = startScroll + delta;

        if (Math.abs(goalScroll - startScroll) < 6) {
          packTitleInputRef.current?.focus({ preventScroll: true });
          return;
        }

        let startTime: number | null = null;
        const step = (now: number) => {
          if (cancelled) return;
          if (startTime === null) startTime = now;
          const elapsed = now - startTime;
          const t = Math.min(1, elapsed / SCROLL_MS);
          container.scrollTop = startScroll + (goalScroll - startScroll) * easeInOutCubic(t);
          if (t < 1) {
            rafScroll = requestAnimationFrame(step);
          } else {
            packTitleInputRef.current?.focus({ preventScroll: true });
          }
        };
        rafScroll = requestAnimationFrame(step);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafLayout1);
      cancelAnimationFrame(rafLayout2);
      cancelAnimationFrame(rafScroll);
    };
  }, [isCreateFor3pl, selectedTenantChildId]);

  // Seleccionar el hook apropiado según el modo
  const isLoading = mode === 'create' ? createPackHook.isCreating : updatePackHook.isUpdating;
  const resetHook = mode === 'create' ? createPackHook.reset : updatePackHook.reset;

  // 🎯 Efecto para cargar datos del pack cuando es modo editar
  useEffect(() => {
    if (mode === 'edit' && pack) {
      setTitle(pack.title);

      // Cargar líneas del pack desde la última versión
      const latestVersion = pack.versions[pack.versions.length - 1];
      if (latestVersion?.lines) {
        const packLines: PackLine[] = latestVersion.lines.map((line) => {
          const fullProduct = products.find((p) => p.id === line.product_id);
          return {
            product_id: line.product_id,
            quantity: line.quantity,
            product_name: fullProduct?.name || line.product?.name || `Producto ${line.product_id}`,
            product_code: fullProduct?.code || line.product?.code,
            product_image: fullProduct?.image_hash || line.product?.image_hash || '',
          };
        });
        setLines(packLines);
      }
    }
  }, [mode, pack, products]);

  const resetForm = () => {
    setTitle('');
    setLines([]);
    setProductToAdd({ product_id: '', quantity: 1 });
    setPack(null);
    setSelectedTenantChildId(null);
    prevTenantChildRef.current = undefined;
    resetHook();
  };

  const handleSelectTenantChild = (value: string) => {
    setSelectedTenantChildId(value ? parseInt(value, 10) : null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const isProductPickerLocked = isCreateFor3pl && !selectedTenantChildId;

  const handleAddProduct = async () => {
    if (!productToAdd.product_id || productToAdd.quantity <= 0) {
      toast({
        title: t('validation.product_required'),
        description: t('validation.quantity_min'),
        variant: 'destructive',
      });
      return;
    }

    const productId = parseInt(productToAdd.product_id);

    // Verificar si el producto ya está añadido
    if (lines.some((line) => line.product_id === productId)) {
      toast({
        title: t('validation.no_duplicates'),
        description: t('validation.no_duplicates'),
        variant: 'destructive',
      });
      return;
    }

    // Obtener información completa del producto
    const fullProduct = products.find((p) => p.id === productId);
    const selectedProductOption = await searchProducts('').then((options) =>
      options.find((p) => p.value === productToAdd.product_id),
    );

    const newLine: PackLine = {
      product_id: productId,
      quantity: productToAdd.quantity,
      product_name: fullProduct?.name || selectedProductOption?.label || `Producto ${productId}`,
      product_code: fullProduct?.code || undefined,
      product_image: fullProduct?.image_hash || '',
    };

    setLines([newLine, ...lines]);

    // Resetear el formulario de agregar
    setProductToAdd({ product_id: '', quantity: 1 });
  };

  const handleRemoveLine = (index: number) => {
    const removedProduct = lines[index];
    setLines(lines.filter((_, i) => i !== index));

    toast({
      title: t('form.remove_product'),
      description: `${removedProduct.product_name || 'Producto'} ${t('form.remove_product')}`,
      variant: 'default',
    });
  };

  const handleLineQuantityChange = (index: number, newQuantity: number) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], quantity: newQuantity };
    setLines(newLines);
  };

  const handleSubmit = async () => {
    try {
      // Validaciones básicas
      if (!title.trim()) {
        toast({
          title: t('validation.title_required'),
          description: t('validation.title_required'),
          variant: 'destructive',
        });
        return;
      }

      if (lines.length === 0) {
        toast({
          title: t('validation.products_required'),
          description: t('validation.products_required'),
          variant: 'destructive',
        });
        return;
      }

      if (isCreateFor3pl && !selectedTenantChildId) {
        toast({
          title: t('validation.client_required_title'),
          description: t('validation.client_required_message'),
          variant: 'destructive',
        });
        return;
      }

      if (mode === 'create') {
        const createRequest: CreatePackRequest = {
          title: title.trim(),
          ...(isCreateFor3pl && selectedTenantChildId ? { team_id: selectedTenantChildId } : {}),
          items: lines.map((line) => ({
            product_id: line.product_id,
            quantity: line.quantity,
          })),
        };

        await createPackHook.createPackAsync(createRequest);
        internalModalRef.current?.closeModal();
        resetForm();
      } else {
        // Actualizar pack existente
        if (!pack) {
          toast({
            title: t('errors.generic'),
            description: t('modal.delete.error_no_selection'),
            variant: 'destructive',
          });
          return;
        }

        const updateRequest: UpdatePackRequest = {
          title: title.trim(),
          items: lines.map((line) => ({
            product_id: line.product_id,
            quantity: line.quantity,
          })),
        };

        await updatePackHook.updatePackAsync(pack.id, updateRequest);
        internalModalRef.current?.closeModal();
        resetForm();
      }
    } catch (error) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} pack:`, error);
    }
  };

  // 🎯 ÚNICO useCallback necesario - Para el modo editar
  const openWithPack = useCallback((packData: Pack) => {
    setPack(packData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithPack, // ← Método nuevo para modo edit
    }),
    [openWithPack],
  );

  // 🎯 Configuración dinámica según el modo
  const config = {
    title: mode === 'create' ? t('modal.create.title') : t('modal.edit.title'),
    submitLabel: mode === 'create' ? t('button.create') : t('button.update'),
    loadingLabel: mode === 'create' ? t('button.creating') : t('button.updating'),
  };

  const submitDisabled = isLoading || lines.length === 0 || (isCreateFor3pl && !selectedTenantChildId);

  // Creación 3PL: primero solo cliente; al elegirlo se muestra el formulario del pack
  const showPackForm = !isCreateFor3pl || selectedTenantChildId !== null;

  return (
    <ModalCenter
      ref={internalModalRef}
      title={config.title}
      type="info"
      width="xl"
      className="max-h-[90vh] h-[calc(100%-30px)] sm:h-auto"
      classNameBody="min-h-0 flex flex-col overflow-hidden"
    >
      <div className="flex flex-col flex-1 min-h-0">
        {/* Contenido que puede crecer */}
        <div ref={modalScrollContainerRef} className="flex-1 min-h-0 space-y-6 overflow-y-auto">
          {/* Selector de cliente (solo 3PL en creación) - Client picker for 3PL create */}
          {isCreateFor3pl && (
            <div className="border border-card-border rounded-lg p-4 shadow-sm">
              <div>
                <h2 className="text-sm font-semibold text-title">{t('clientSelector.title')}</h2>
                <p className="text-sm text-complementary-text mt-1">{t('clientSelector.description')}</p>
              </div>
              <div className="mt-4">
                <Combobox
                  options={clientOptions}
                  placeholder={hasClientOptions ? t('clientSelector.placeholder') : t('clientSelector.empty')}
                  value={selectedTenantChildId ? String(selectedTenantChildId) : ''}
                  setValue={handleSelectTenantChild}
                  className="w-full"
                  disabled={!hasClientOptions || tenantChildrenLoading}
                  dataTestId="pack-modal-client-selector"
                />
              </div>
              <div className="mt-4 space-y-2">
                {tenantChildrenError && (
                  <Alert className="border-error text-error">
                    <AlertDescription>{tenantChildrenError}</AlertDescription>
                  </Alert>
                )}
                {!hasClientOptions && !tenantChildrenLoading && (
                  <Alert className="border-warning text-warning">
                    <AlertDescription>{t('clientSelector.emptyDescription')}</AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

          {showPackForm && (
            <>
              {/* Título del pack - ancla scroll al elegir cliente (3PL) */}
              <div ref={packTitleSectionRef} className="space-y-2 scroll-mt-4">
                <label className="text-sm font-medium text-text">{t('form.title')} *</label>
                <Input
                  ref={packTitleInputRef}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('form.title_placeholder')}
                  className="w-full"
                  data-testid="pack-title-input"
                />
              </div>

              {/* Formulario para agregar productos */}
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  {/* Selector de producto con imagen, nombre y código */}
                  <div className="flex-1 space-y-2">
                    <label className="text-sm font-medium text-text">{t('form.products')} *</label>
                    <ProductCombobox
                      value={productToAdd.product_id}
                      setValue={(value) => setProductToAdd({ ...productToAdd, product_id: value })}
                      placeholder={t('product_selector.default_placeholder')}
                      searchFn={searchProductsForCombobox}
                      className="w-full"
                      dataTestId="product-selector"
                      disabled={isProductPickerLocked || productsLoading}
                    />
                  </div>

                  {/* Cantidad y botón añadir */}
                  <div className="flex gap-3 items-end">
                    <div className="w-full sm:w-32 space-y-2">
                      <label className="text-sm font-medium text-text">
                        {t('form.quantity')} <span className="text-destructive">*</span>
                      </label>
                      <StepperInput
                        value={productToAdd.quantity}
                        onChange={(value) => setProductToAdd({ ...productToAdd, quantity: value })}
                        min={1}
                        max={1000}
                        className="w-full"
                        data-testid="quantity-stepper"
                        disabled={isProductPickerLocked}
                      />
                    </div>

                    <Button
                      onClick={handleAddProduct}
                      variant="primary"
                      size="sm"
                      className="gap-2 h-10 px-3 sm:px-4 shrink-0"
                      disabled={!productToAdd.product_id || isProductPickerLocked || productsLoading}
                      icon={faPlus}
                      data-testid="add-product-button"
                    />
                  </div>
                </div>
              </div>

              {/* Lista de productos añadidos */}
              {lines.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">{t('form.products')}</label>
                    <span className="text-xs text-muted-foreground">
                      {t('card.products_count', {
                        count: lines.length,
                        plural: lines.length !== 1 ? 's' : '',
                      })}
                    </span>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {lines.map((line, index) => (
                      <div
                        key={`${line.product_id}-${index}`}
                        className="flex items-center gap-3 p-3 sm:p-4 border-b border-card-border last:border-b-0 hover:bg-muted/20 transition-colors"
                      >
                        {/* Componente Product - a la izquierda */}
                        <div className="flex-1 min-w-0">
                          <Product
                            id={line.product_id}
                            name={line.product_name || `Producto ${line.product_id}`}
                            code={line.product_code}
                            src={line.product_image || ''}
                            acronym={line.product_name
                              ?.split(' ')
                              .map((word) => word[0])
                              .join('')
                              .toUpperCase()
                              .slice(0, 2)}
                            enableLineClamp={true}
                            data-testid={`pack-product-${index}`}
                          />
                        </div>

                        {/* Controles pegados a la derecha */}
                        <div className="flex items-center gap-4 shrink-0">
                          {/* Selector de cantidad */}
                          <div className="w-36">
                            <StepperInput
                              value={line.quantity}
                              onChange={(value) => handleLineQuantityChange(index, value)}
                              min={1}
                              max={1000}
                              className="w-full"
                              data-testid={`line-quantity-${index}`}
                            />
                          </div>

                          {/* Botón de eliminar */}
                          <button
                            onClick={() => handleRemoveLine(index)}
                            className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                            type="button"
                            data-testid={`remove-pack-line-${index}`}
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4 text-icons" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {lines.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center space-y-3 rounded-lg">
                  <div className="w-12 h-12 bg-muted-foreground/10 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  </div>
                  <div className="space-y-1">
                    <p className="text-md font-medium text-foreground">{t('form.products_empty')}</p>
                    <p className="text-sm text-muted-foreground">{t('form.products_empty_description')}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Botones fijos en la parte inferior */}
        <div className="flex-shrink-0 pt-4">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={handleCancel}
              disabled={isLoading}
              className="w-full sm:w-auto"
              data-testid="pack-modal-cancel"
            >
              {t('button.cancel')}
            </Button>
            {showPackForm && (
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="w-full sm:w-auto"
                data-testid="pack-modal-submit"
              >
                {isLoading ? config.loadingLabel : config.submitLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});

PackModal.displayName = 'PackModal';

export default PackModal;
